import React, { useEffect, useState, useRef } from 'react';
import { Message, ChatState } from '../../types/chat';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { chatService } from '../../lib/chat/chatService';
import { supabase } from '../../lib/supabase';
import AguiMessageList from "./aguiMessageList";

interface ChatContainerProps {
  tournamentId: string;
  isAdmin?: boolean;
}

const MESSAGES_LIMIT = 100;
const AUTO_RECONNECT_DELAY = 5000;

export function ChatContainer({ tournamentId, isAdmin }: ChatContainerProps) {
  const [state, setState] = useState<ChatState>({
    messages: [],
    isConnected: false,
    isOffline: !navigator.onLine
  });

  // Track processed message IDs to prevent duplicates
  const processedMessages = useRef(new Set<string>());

  // Load initial messages
  useEffect(() => {
    const loadMessages = async () => {
      if (!supabase || !tournamentId) return;

      try {
        const { data, error } = await supabase
          .from('messages')
          .select('*')
          .eq('tournament_id', tournamentId)
          .order('created_at', { ascending: false })
          .limit(MESSAGES_LIMIT);

        if (error) throw error;
        
        // Add initial message IDs to processed set
        (data || []).forEach(msg => processedMessages.current.add(msg.id));

        // Format messages
        const formattedMessages = (data || []).map(msg => ({
          id: msg.id,
          senderId: msg.sender_id,
          senderName: msg.sender_name,
          content: msg.content,
          type: msg.type,
          isAdmin: msg.is_admin,
          createdAt: msg.created_at,
          tournamentId: msg.tournament_id,
          metadata: msg.metadata
        })).reverse();

        setState(prev => ({
          ...prev,
          messages: formattedMessages,
          isConnected: true
        }));
      } catch (error) {
        console.error('Failed to load messages:', error);
      }
    };

    // Set up auto-reconnect
    const reconnectTimer = setInterval(() => {
      if (!state.isConnected) {
        loadMessages();
      }
    }, AUTO_RECONNECT_DELAY);

    loadMessages();
    
    // Connect to chat service
    chatService.connect();
    
    // Set up real-time subscription for message changes
    const subscription = supabase?.channel('messages')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `tournament_id=eq.${tournamentId}`
        },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            setState(prev => ({
              ...prev,
              messages: prev.messages.filter(m => m.id !== payload.old.id)
            }));
            processedMessages.current.delete(payload.old.id);
          } else if (payload.eventType === 'INSERT') {
            // Add new message
            const message: Message = {
              id: payload.new.id,
              senderId: payload.new.sender_id,
              senderName: payload.new.sender_name,
              content: payload.new.content,
              type: payload.new.type,
              isAdmin: payload.new.is_admin,
              createdAt: payload.new.created_at,
              tournamentId: payload.new.tournament_id,
              metadata: payload.new.metadata
            };
            
            // Skip if already processed
            if (!processedMessages.current.has(message.id)) {
              processedMessages.current.add(message.id);
              setState(prev => ({
                ...prev,
                messages: [...prev.messages, message]
              }));
            }
          }
        }
      )
      .subscribe();

    // Subscribe to messages
    chatService.onMessage(message => {
      // Skip if message already processed
      if (processedMessages.current.has(message.id)) {
        return;
      }

      // Add to processed set
      processedMessages.current.add(message.id);

      setState(prev => ({
        ...prev,
        messages: [...prev.messages, message],
        isConnected: true
      }));
    });

    // Subscribe to connection status
    chatService.onConnectionChange(isConnected => {
      setState(prev => ({
        ...prev,
        isConnected
      }));
    });

    // Handle online/offline status
    const handleOnline = () => {
      setState(prev => ({ ...prev, isOffline: false }));
    };
    const handleOffline = () => {
      setState(prev => ({ ...prev, isOffline: true }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (subscription) {
        subscription.unsubscribe();
      }
      clearInterval(reconnectTimer);
      chatService.disconnect();
      processedMessages.current.clear();
    };
  }, [tournamentId]);

  return (
    <div className="chat-container">
      {/* Render AG-UI system messages above the regular chat */}
      <AguiMessageList />
      {/* Existing chat UI follows */}
      <div className="flex flex-col h-[calc(100vh-env(safe-area-inset-bottom)-8rem)] bg-gray-50 overscroll-contain">
        <div className="flex justify-between items-center p-4 border-b bg-white">
          <h2 className="text-lg font-semibold">Tournament Chat</h2>
        </div>

        <MessageList
          messages={state.messages}
          isConnected={state.isConnected}
          isOffline={state.isOffline}
        />
        
        <MessageInput
          isAdmin={isAdmin}
          tournamentId={tournamentId}
        />
      </div>
    </div>
  );
}